import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
} from '@mui/material';
import { normalizeFromEndpoint } from '../../../../utils/endpointUtils';
import BlandAIConfig from './BlandAIConfig';
import VAPIConfig from './VAPIConfig';
import ElevenLabsConfig from './ElevenLabsConfig';
import TwilioConfig from './TwilioConfig';

const VoiceConfig = ({
  config,
  onConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onDelete,
}) => {
  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handleEndpointChange = (event) => {
    const normalizedEndpoint = normalizeFromEndpoint(event.target.value);
    onEndpointChange(normalizedEndpoint);
  };

  const renderPlatformConfig = () => {
    const commonProps = {
      config,
      onConfigChange,
      communicationEndpoints,
      onEndpointChange: handleEndpointChange,
    };

    switch (config.platform || 'bland_ai') {
      case 'bland_ai':
        return <BlandAIConfig {...commonProps} />;
      case 'vapi':
        return <VAPIConfig {...commonProps} />;
      case 'elevenlabs':
        return <ElevenLabsConfig {...commonProps} />;
      case 'twilio':
        return <TwilioConfig {...commonProps} />;
      default:
        return <BlandAIConfig {...commonProps} />;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Voice Configuration
      </Typography>

      {/* Platform Selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Platform</InputLabel>
        <Select
          value={config.platform || 'bland_ai'}
          onChange={handleConfigChange('platform')}
          label="Platform"
        >
          <MenuItem value="bland_ai">Bland AI</MenuItem>
          <MenuItem value="vapi">VAPI</MenuItem>
          <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
          <MenuItem value="twilio">Twilio</MenuItem>
        </Select>
      </FormControl>

      {/* From Number - Common across all platforms */}
      <FormControl fullWidth margin="normal">
        <InputLabel>From Number</InputLabel>
        <Select
          value={config.from_endpoint || ''}
          onChange={handleEndpointChange}
          label="From Number"
        >
          {communicationEndpoints
            .filter(endpoint => endpoint.channel_list && endpoint.channel_list.includes('voice'))
            .map(endpoint => (
              <MenuItem key={endpoint.id} value={endpoint.id}>
                {endpoint.value}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Platform-specific configuration */}
      <Paper sx={{ p: 2, mt: 2 }}>
        {renderPlatformConfig()}
      </Paper>
    </Box>
  );
};

VoiceConfig.propTypes = {
  config: PropTypes.shape({
    platform: PropTypes.oneOf(['bland_ai', 'vapi', 'elevenlabs', 'twilio']),
    from_endpoint: PropTypes.string,
    // Other fields will be validated by platform-specific components
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      channel_list: PropTypes.arrayOf(PropTypes.string).isRequired,
      platform: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired,
        platform_type: PropTypes.string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  onEndpointChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default VoiceConfig; 